/* eslint-disable no-param-reassign */
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  Modifier,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { Popover } from '@headlessui/react';
import {
  AdjustmentsHorizontalIcon,
  ArrowRightIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  CubeIcon,
  DocumentDuplicateIcon,
  MapPinIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import store from 'app/store';
import { useContextMenu } from 'components/common/context-menu-manager';
import KeyboardMouseShortcutsPopup from 'components/common/keyboard-mouse-shortcuts-popup';
import TonnageConflictDialog from 'components/common/tonnage-conflict-dialog';
import View3DHelpPopup from 'components/common/view-3d-help-popup';
import RotateLeftIcon from 'components/icons/rotate-left-icon';
import RotateRightIcon from 'components/icons/rotate-right-icon';
import StepSidebar from 'components/layout/step-sidebar';
import Tooltip from 'components/layout/tooltip';
import SendPhotoCollectorModal from 'components/project-page/modals/send-photo-collector-modal';
import StructureGrid from 'components/structure-page/2d/structure-grid';
import Grid3DScene from 'components/structure-page/3d/grid-3d-scene';
import Compass from 'components/structure-page/compass';
import CondenserLocationsOverlay from 'components/structure-page/condenser-locations-overlay';
import FootprintModeToolbar from 'components/structure-page/footprint-mode-toolbar';
import FootprintResetButton from 'components/structure-page/footprint-reset-button';
import FootprintShapeSelector from 'components/structure-page/footprint-shape-selector';
import LevelPicker from 'components/structure-page/level-picker';
import ApplianceList from 'components/structure-page/lists/appliance-list';
import EquipmentPlacementList from 'components/structure-page/lists/equipment-placement-list';
import FenestrationList from 'components/structure-page/lists/fenestration-list';
import SpaceList from 'components/structure-page/lists/space-list';
import WindowGenerator from 'components/structure-page/lists/window-generator';
import AddEquipmentModal from 'components/structure-page/modals/add-equipment-modal';
import AttributeModal from 'components/structure-page/modals/attribute-modal';
import CropImageModal from 'components/structure-page/modals/crop-image-modal';
import SpaceTypePickerModal from 'components/structure-page/modals/space-type-picker-modal';
import SpaceDrawModeToolbar from 'components/structure-page/space-draw-mode-toolbar';
import {
  DefaultsStep,
  LayoutImagesList,
  LayoutImagesUploadStep,
  LevelDetailsAccordion,
  StructureDetailsStep
} from 'components/structure-page/steps';
import StructureDragOverlay from 'components/structure-page/structure-drag-overlay';
import StructureMetricsDashboard from 'components/structure-page/structure-metrics-dashboard';
import SunDirectionOverlay from 'components/structure-page/sun-direction-overlay';
import { MIN_SPACE_DIM_FEET, STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  ActiveItemType,
  ActiveTabType,
  ExposureDirectionType,
  FootprintPolygonsType,
  GridObjectType,
  GridSpaceDataType,
  LevelType,
  PartialWithRequired,
  PositionAndDimensionsType,
  SidebarSectionType,
  SpaceType,
  StructureImageTransformType
} from 'data/types';
import { useUpdateStructureImageTransformMutation } from 'features/api/project-api';
import { setProjectSettingValue, updateProjectSetting } from 'features/project';
import {
  autoAddFireplaceAppliances,
  autoGenerateFullDuctworkSpaces,
  autoGenerateHybridFullLevelSpacesThunk,
  autoMirrorOpenSpacesToUnconditionedLevels,
  beginUndoBatch,
  clearStructureHistory,
  endUndoBatch,
  scaleAllLevelFootprints,
  scaleAllSpacesWithImage,
  setSatelliteView,
  updateStructureImageTransform,
  updateStructureItem,
  updateStructureItemAttribute
} from 'features/structure';
import { setSystemTonnageRecord } from 'features/structure/structure-slice';
import {
  addVisitedStructureStep,
  setActiveItem,
  setActiveLevelDetailIndex,
  setActiveLevelIndex,
  setActiveStructureStepId,
  setActiveSubStepIndex,
  setActiveTab
} from 'features/ui';
import { useAddSpacesInitialRecenter } from 'hooks/use-add-spaces-initial-recenter';
import { useFootprintEditing } from 'hooks/use-footprint-editing';
import useHybridStructureAutobuild from 'hooks/use-hybrid-structure-autobuild';
import useIsSimpleSizingPanel from 'hooks/use-is-simple-sizing-panel';
import { useKeyShortcuts } from 'hooks/use-key-shortcuts';
import { useMarkStructureStepCompleted } from 'hooks/use-mark-structure-step-completed';
import { useSpaceClipboard } from 'hooks/use-space-clipboard';
import { useSpaceDrawing } from 'hooks/use-space-drawing';
import useSpaceOpacity from 'hooks/use-space-opacity';
import { useStructureHistory } from 'hooks/use-structure-history';
import useStructureTonnage from 'hooks/use-structure-tonnage';
import useSystemDesignBypassMode from 'hooks/use-system-design-bypass-mode';
import { useStructureGrid } from 'hooks/useStructureGrid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { getSpace3DGridPosition } from 'utils/3d';
import axios from 'utils/api';
import {
  needsAutoMirror,
  needsFullDuctworkSpaceGeneration,
  needsHybridFullLevelSpaceGeneration
} from 'utils/auto-mirror-level-layout';
import { shouldHideBasicInfoProgressBar } from 'utils/basic-info-progress-bar';
import { loadStructureAttachments } from 'utils/data';
import { createHandleDragEnd, getActiveDraggedItem } from 'utils/drag-and-drop';
import {
  equipmentPreferencesPageVisible,
  existingEquipmentPageVisible,
  structureBackTab
} from 'utils/existing-equipment-page-visibility';
import {
  computeAutoScaleParams,
  getConditionedLevelCount,
  getFirstMainLevelIndex,
  getTotalFootprintArea,
  resolveAutoScalePivot,
  resolveFootprintStepEntryScaleAction,
  shouldShowAutoScaleForArea,
  wouldAutoScaleChangeAnyFootprint
} from 'utils/footprint-auto-scale';
import { resolveActiveFootprintMode } from 'utils/footprint-mode';
import {
  getEffectiveFootprintPolygons,
  hasFootprintPolygons,
  isRectInAnyPolygon
} from 'utils/footprint-polygons';
import {
  getScaledFootprintArea,
  transformFootprintPolygons
} from 'utils/footprint-scaling';
import { FootprintScalingTransform } from 'utils/footprint-scaling-gesture';
import {
  areSpacesInViewport,
  getGridObjectsForLevel,
  getGridSpacesForLevel,
  getOverlaySpacesForLevel
} from 'utils/grid';
import {
  snapTransformToGridWithZoom,
  snapTransformToGridWithZoomAndOrigin
} from 'utils/grid-snap';
import { computeZoomToFitView, imagePixelBounds } from 'utils/grid-zoom';
import { resizeImageIfNeeded } from 'utils/helpers';
import {
  computeScaledImageTransformAroundPivot,
  loadImageDimensions
} from 'utils/image-scale';
import { formatShortcutLabel } from 'utils/key-shortcuts';
import { resolveLevelLayoutAttachmentId } from 'utils/level-layout-image';
import { openingAwareCollisionDetection } from 'utils/opening-collision';
import { resolveStackedLevelImageTransforms } from 'utils/resolve-stacked-level-image-transforms';
import {
  isDuctedOnlySystemPreference,
  isDuctedSystemSelected,
  isHybridOnlySystemPreference
} from 'utils/project';
import { resolvePropertyCoordinates } from 'utils/property';
import {
  fetchSatelliteImageAsFile,
  SATELLITE_DEFAULT_ZOOM,
  SATELLITE_IMAGE_FILE_NAME,
  SATELLITE_IMAGE_NAME
} from 'utils/satellite-image';
import { appendSatelliteViewToFormData } from 'utils/satellite-view';
import { SidePanelTab } from 'utils/side-panel-tabs';
import {
  gridSpaceFromScaledSpace,
  scaleSpaceGeometry,
  snapshotSpaceGeometries,
  SpaceGeometry
} from 'utils/space-scaling';
import {
  findStructureItemById,
  levelHasRequiredLevelDetailsAttributes
} from 'utils/structure';
import {
  getAddSpacesNavigableLevels,
  isAddSpacesLevelComplete
} from 'utils/structure-add-spaces';
import {
  getDefaultConditionedLevelIndex,
  getFirstConditionedLevelIndexInList
} from 'utils/structure-default-level';
import { shouldClearStructureHistoryOnUniqueIdChange } from 'utils/structure-history';
import {
  getFootprintStepLevelIndices,
  getFootprintStepSubStepIndex,
  isFootprintLevelComplete
} from 'utils/footprint-step-navigation';
import {
  getLevelDetailLevels,
  getPagerLevelsForStep
} from 'utils/structure-level-pager';
import {
  getGridModeForSpaceDetailsCategory,
  getSpaceDetailsCategories,
  getSpaceDetailsCategoryLabel,
  getSpaceDetailsSubSteps
} from 'utils/structure-space-details';
import { SpaceDetailsCategory } from 'utils/structure-space-details-types';
import { getSubStepDrivenKey } from 'utils/structure-sub-step-driven-key';
import { isSystemDesignLocked } from 'utils/system-design-bypass';
import {
  adoptCalcTonnageRecord,
  isUnsureTonnage,
  shouldOpenTonnageConflict,
  TonnageRecord
} from 'utils/tonnage-logic';
import { persistTonnageRecord } from 'utils/tonnage-persist';
import {
  generateStructureAccuracyData,
  getModifierKey,
  warningModal
} from 'utils/ui';
import { waitForElementSize } from 'utils/wait-for-element-size';

const STEPS_WITH_OWN_LEVEL_DEFAULT = new Set([
  'footprint',
  'add_spaces',
  'space_details'
]);

const RECENTER_VIEW_SHORTCUT_KEY = 'R';

export default function StructurePage() {
  const dispatch = useAppDispatch();
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const { selectedSystems } = useAppSelector((state) => state.systems);
  const {
    activeItem,
    manuallyCompletedSteps,
    activeLevelIndex,
    activeLevelDetailIndex,
    activeSubStepIndex,
    showZones,
    activeTab,
    activeStructureStepId: currentSectionId
  } = useAppSelector((state) => state.ui);
  const { defaultSizingItemsVersion, modifiedDefaultCategories } =
    useAppSelector((state) => state.data);
  const { projectSettings, propertyLocation, attomData, totalBTUs } =
    useAppSelector((state) => state.project);
  const {
    structure,
    structureAttachments,
    structureTypeId,
    structureUniqueId,
    structureImageAssignments,
    footprint,
    existingSystems
  } = useAppSelector((state) => state.structure);
  const autoGenerateSizingItems = useMemo(() => {
    const setting = projectSettings.find(
      (ps) => ps.code === 'auto_generate_structure_sizing'
    );
    return setting?.value !== 'No';
  }, [projectSettings]);

  const autoApplySpaceAboveAndBelow = useMemo(() => {
    const setting = projectSettings.find(
      (ps) => ps.code === 'auto_apply_space_above_and_below'
    );
    return setting?.value !== 'No';
  }, [projectSettings]);

  const attributeModalState = useState(false);
  const [, setAttributeModalOpen] = attributeModalState;
  const setCurrentSectionId = useCallback(
    (id: string) => dispatch(setActiveStructureStepId(id)),
    [dispatch]
  );
  const markStructureStepCompleted = useMarkStructureStepCompleted();
  // Hybrid-only deterministic auto-build of the empty structure (AHA-6784).
  useHybridStructureAutobuild();
  const [previousActiveItem, setPreviousActiveItem] =
    useState<ActiveItemType | null>(null);
  const {
    activeDragData,
    setActiveDragData,
    activeId,
    setActiveId,
    draggedSectionSpaces,
    setDraggedSectionSpaces,
    show3DView,
    setShow3DView,
    gridZoom,
    setGridZoom,
    gridPanOffset,
    setGridPanOffset,
    gridZoomRef,
    gridPanOffsetRef
  } = useStructureGrid();
  const [isMultiSelectSpaceDrag, setIsMultiSelectSpaceDrag] = useState(false);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<Set<string>>(
    new Set()
  );
  const [layoutUnderlayOpacity, setLayoutUnderlayOpacity] = useState(50);
  const [gridLinesOpacity, setGridLinesOpacity] = useState(50);
  const [inactiveLevelOpacity, setInactiveLevelOpacity] = useState(50);
  const [overlaySpacesOpacity, setOverlaySpacesOpacity] = useState(50);
  const [spaceOpacity, setSpaceOpacity] = useSpaceOpacity();
  const [showRecenterButton, setShowRecenterButton] = useState(true);
  const [localImageTransform, setLocalImageTransform] =
    useState<StructureImageTransformType | null>(null);
  const [isLayoutImageSelected, setIsLayoutImageSelected] = useState(false);
  const [localFootprintTransform, setLocalFootprintTransform] =
    useState<FootprintScalingTransform | null>(null);
  const [autoResizeSpacesToFit, setAutoResizeSpacesToFit] = useState(true);
  const [footprintImageRecenterNonce, setFootprintImageRecenterNonce] =
    useState(0);
  const footprintRecenterDoneKeyRef = useRef<string | null>(null);
  const [cropOnDropOpen, setCropOnDropOpen] = useState(false);
  const [cropOnDropData, setCropOnDropData] = useState<{
    attachmentId: number | null;
    attachmentUrl: string | null;
    attachmentName: string | null;
    targetLevelUniqueId: string | null;
  }>({
    attachmentId: null,
    attachmentUrl: null,
    attachmentName: null,
    targetLevelUniqueId: null
  });
  const activeItemRef = useRef(activeItem);
  activeItemRef.current = activeItem;
  const structureGridRef = useRef<HTMLDivElement>(null);
  const defaultLevelSectionRef = useRef<string | null>(null);
  const hasAutoMirroredUnconditionedLevelsRef = useRef(false);
  const hasGeneratedFullDuctworkSpacesRef = useRef(false);
  const [updateImageTransform] = useUpdateStructureImageTransformMutation();
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const satellitePullAttemptedRef = useRef<string | null>(null);
  const [photoCollectorOpen, setPhotoCollectorOpen] = useState(false);
  const imageScaleBeforeDragRef = useRef<number | null>(null);
  const imagePositionBeforeDragRef = useRef<{ x: number; y: number } | null>(
    null
  );
  const footprintBeforeDragRef = useRef<FootprintPolygonsType | null>(null);
  const spacesBeforeDragRef = useRef<Map<string, SpaceGeometry> | null>(null);
  const structureImageAssignmentsRef = useRef(structureImageAssignments);
  structureImageAssignmentsRef.current = structureImageAssignments;
  const structureAttachmentsRef = useRef(structureAttachments);
  structureAttachmentsRef.current = structureAttachments;
  const levels = useMemo(() => [...structure], [structure]);

  const structureAccuracySignature = useMemo(
    () =>
      JSON.stringify(
        structure.map((level) => ({
          conditioned: level.conditioned,
          attributes: level.attributes,
          electricalPanels: level.electricalPanels,
          sections: level.sections.map((section) => ({
            spaces: section.spaces.map((space) => ({
              attributes: space.attributes,
              sizingAttributes: space.sizingAttributes
            }))
          }))
        }))
      ),
    [structure]
  );

  const structureActiveItemAttributesSignature = useMemo(
    () =>
      JSON.stringify(
        structure.map((level) => ({
          attributes: level.attributes,
          sections: level.sections.map((section) => ({
            spaces: section.spaces.map((space) => ({
              attributes: space.attributes
            }))
          }))
        }))
      ),
    [structure]
  );
  const selectedLevelIndex = activeLevelIndex;
  const activeLevel = levels[selectedLevelIndex];
  const {
    footprintMode,
    setFootprintMode,
    drawingResetKey,
    layoutImageDimsRef,
    effectiveFootprintPolygons,
    effectiveFootprintShapes,
    handleFootprintShapeChange,
    handleDeleteFootprintShape,
    canAddFootprintShape,
    handleAddFootprintShape,
    handleFootprintStrokeComplete,
    handleRotateFootprint,
    handleFootprintDragStart,
    handleFootprintDragEnd,
    handleResetFootprint,
    handleCopyLevelBelow,
    handleCopyLevelAbove,
    levelBelowFootprint,
    levelAboveFootprint
  } = useFootprintEditing(activeLevel);
  const nonLayoutStepIds = [
    'configure_level_details',
    'confirm_details',
    'images'
  ];
  const hideSizingAccuracyStepIds = ['configure_level_details', 'images'];
  const isOnLayoutSection = !nonLayoutStepIds.includes(currentSectionId);

  const isAddSpacesStep = currentSectionId === 'add_spaces';
  const isImagesStep = currentSectionId === 'images';
  const isSpaceDetailsStep = currentSectionId === 'space_details';
  const isFootprintStep = currentSectionId === 'footprint';
  const {
    spaceDrawMode,
    setSpaceDrawMode,
    isDrawingSpace,
    pendingDrawnSpace,
    typePickerOpenState: spaceTypePickerOpenState,
    handleSpaceStrokeComplete,
    handleDrawnSpaceTypeSelect,
    handleCancelDrawnSpace
  } = useSpaceDrawing({ activeLevel, isAddSpacesStep });

  const addSpacesNavigableLevels = useMemo(
    () => getAddSpacesNavigableLevels(structure),
    [structure]
  );
  const spaceDetailsSubSteps = useMemo(
    () => getSpaceDetailsSubSteps(structure),
    [structure]
  );

  const addSpacesIndex = Math.min(
    Math.max(activeSubStepIndex, 0),
    Math.max(addSpacesNavigableLevels.length - 1, 0)
  );
  const spaceDetailsIndex = Math.min(
    Math.max(activeSubStepIndex, 0),
    Math.max(spaceDetailsSubSteps.length - 1, 0)
  );
  const currentSpaceDetailsSubStep = spaceDetailsSubSteps[spaceDetailsIndex];
  const currentSpaceDetailsCategory = currentSpaceDetailsSubStep?.category;

  const handleLayoutLevelClick = useCallback(
    (level: LevelType) => {
      const levelIndex = structure.findIndex(
        (l) => l.uniqueId === level.uniqueId
      );
      if (levelIndex !== -1) {
        dispatch(setActiveLevelIndex(levelIndex));
      }
      if (isAddSpacesStep) {
        const index = addSpacesNavigableLevels.findIndex(
          (l) => l.uniqueId === level.uniqueId
        );
        if (index !== -1) {
          dispatch(setActiveSubStepIndex(index));
        }
        return;
      }
      if (isSpaceDetailsStep) {
        const index = spaceDetailsSubSteps.findIndex(
          (subStep) =>
            subStep.category === currentSpaceDetailsCategory &&
            subStep.level.uniqueId === level.uniqueId
        );
        if (index !== -1) {
          dispatch(setActiveSubStepIndex(index));
        }
      }
    },
    [
      isAddSpacesStep,
      isSpaceDetailsStep,
      addSpacesNavigableLevels,
      spaceDetailsSubSteps,
      currentSpaceDetailsCategory,
      structure,
      dispatch
    ]
  );

  const renderSpaceDetailsPanel = useCallback(
    (category: SpaceDetailsCategory | undefined) => {
      if (category === 'ductwork') {
        return (
          <div className='flex flex-col gap-3 p-4'>
            <p className='text-sm text-gray-600'>
              Only levels with partial ductwork can be edited here. For each
              one, uncheck any space that is not served by the existing ductwork
              (no duct register). Spaces are marked as served by default.
            </p>
          </div>
        );
      }
      if (category === 'appliances') {
        return (
          <div className='p-4'>
            <ApplianceList />
          </div>
        );
      }
      return (
        <div className='p-4'>
          <WindowGenerator />
          <FenestrationList />
        </div>
      );
    },
    []
  );

  const spaceDetailsCategories = useMemo(
    () => getSpaceDetailsCategories(structure),
    [structure]
  );

  const spaceDetailsTabs: SidePanelTab[] = useMemo(
    () =>
      spaceDetailsCategories.map((category) => ({
        id: category,
        label: getSpaceDetailsCategoryLabel(category),
        renderContent: () => renderSpaceDetailsPanel(category)
      })),
    [spaceDetailsCategories, renderSpaceDetailsPanel]
  );

  const handleSpaceDetailsTabChange = useCallback(
    (tabId: string) => {
      const category = tabId as SpaceDetailsCategory;
      const currentLevelUniqueId = currentSpaceDetailsSubStep?.level.uniqueId;
      const sameLevelIndex = spaceDetailsSubSteps.findIndex(
        (subStep) =>
          subStep.category === category &&
          subStep.level.uniqueId === currentLevelUniqueId
      );
      const targetIndex =
        sameLevelIndex !== -1
          ? sameLevelIndex
          : spaceDetailsSubSteps.findIndex(
              (subStep) => subStep.category === category
            );
      if (targetIndex !== -1) {
        dispatch(setActiveSubStepIndex(targetIndex));
      }
    },
    [spaceDetailsSubSteps, currentSpaceDetailsSubStep, dispatch]
  );

  const {
    undo,
    redo,
    canUndo,
    canRedo,
    clear: clearHistory
  } = useStructureHistory(isOnLayoutSection);

  const modKey = useMemo(() => getModifierKey(), []);

  useSpaceClipboard({
    enabled: isOnLayoutSection,
    selectedSpaceIds,
    structure,
    activeLevelIndex,
    dispatch,
    setSelectedSpaceIds
  });

  useEffect(() => {
    return () => {
      dispatch(clearStructureHistory());
    };
  }, [dispatch]);

  const previousStructureUniqueIdRef = useRef<string | null>(null);

  useEffect(() => {
    const previous = previousStructureUniqueIdRef.current;
    previousStructureUniqueIdRef.current = structureUniqueId;
    if (
      shouldClearStructureHistoryOnUniqueIdChange(previous, structureUniqueId)
    ) {
      clearHistory();
    }
  }, [structureUniqueId, clearHistory]);

  const currentLayoutImageAttachmentId = useMemo(() => {
    if (structureAttachments.length === 0) {
      return null;
    }

    return resolveLevelLayoutAttachmentId({
      structure,
      activeLevelIndex,
      structureImageAssignments
    });
  }, [
    structureAttachments,
    structureImageAssignments,
    structure,
    activeLevelIndex
  ]);

  useEffect(() => {
    setOverlaySpacesOpacity(currentLayoutImageAttachmentId !== null ? 0 : 50);
  }, [currentLayoutImageAttachmentId]);

  const layoutImageForCurrentLevel = useMemo(() => {
    if (!currentLayoutImageAttachmentId) {
      return undefined;
    }

    const attachment = structureAttachments.find(
      (a) => a.id === currentLayoutImageAttachmentId
    );

    return attachment?.url ?? undefined;
  }, [structureAttachments, currentLayoutImageAttachmentId]);

  const structureOrientation = useMemo(() => {
    const orientationSetting = projectSettings.find(
      (s) => s.code === 'structure_orientation'
    );
    return orientationSetting?.value as ExposureDirectionType;
  }, [projectSettings]);

  const layoutImageTransformForCurrentLevel = useMemo(() => {
    if (localImageTransform !== null) {
      return localImageTransform;
    }

    if (!currentLayoutImageAttachmentId) {
      return undefined;
    }

    const assignment =
      structureImageAssignments[currentLayoutImageAttachmentId];
    if (!assignment) {
      return undefined;
    }

    return {
      scale: assignment.scale,
      rotation: assignment.rotation,
      startPositionX: assignment.startPositionX,
      startPositionY: assignment.startPositionY
    };
  }, [
    currentLayoutImageAttachmentId,
    structureImageAssignments,
    localImageTransform
  ]);

  const allObjects3D = useMemo(() => {
    const objects: ReturnType<typeof getGridObjectsForLevel> = [];
    for (let levelIndex = 0; levelIndex < structure.length; levelIndex += 1) {
      const levelObjects = getGridObjectsForLevel(structure, levelIndex, {
        configurations
      });
      objects.push(...levelObjects);
    }
    return objects.filter(
      (obj) => obj.type !== 'condenser' && obj.type !== 'air-handler'
    );
  }, [structure, configurations]);

  const sectionColors = useMemo(() => {
    const colorMap = new Map<
      string,
      { bg: string; border: string; text: string }
    >();

    if (!showZones) return colorMap;

    const baseColors = [
      { hue: 30, saturation: 75, lightness: 65 },
      { hue: 60, saturation: 75, lightness: 65 },
      { hue: 120, saturation: 75, lightness: 65 },
      { hue: 270, saturation: 75, lightness: 65 },
      { hue: 180, saturation: 75, lightness: 65 },
      { hue: 210, saturation: 75, lightness: 65 },
      { hue: 240, saturation: 75, lightness: 65 },
      { hue: 300, saturation: 75, lightness: 65 },
      { hue: 330, saturation: 75, lightness: 65 },
      { hue: 15, saturation: 75, lightness: 65 },
      { hue: 45, saturation: 75, lightness: 65 },
      { hue: 90, saturation: 75, lightness: 65 },
      { hue: 150, saturation: 75, lightness: 65 },
      { hue: 200, saturation: 75, lightness: 65 }
    ];

    const uniqueSections: Array<{ uniqueId: string }> = [];
    const seenIds = new Set<string>();

    structure.forEach((level) => {
      level.sections.forEach((section) => {
        if (!seenIds.has(section.uniqueId)) {
          seenIds.add(section.uniqueId);
          uniqueSections.push({ uniqueId: section.uniqueId });
        }
      });
    });

    uniqueSections.sort((a, b) => a.uniqueId.localeCompare(b.uniqueId));

    const generateUniqueColor = (index: number) => {
      if (index < baseColors.length) {
        return baseColors[index];
      }

      const hueStep = 360 / Math.max(uniqueSections.length, baseColors.length);
      const hue = (index * hueStep) % 360;
      const saturation = 70 + (index % 3) * 5;
      const lightness = 60 + (index % 4) * 3;

      return {
        hue: Math.round(hue),
        saturation: Math.max(65, Math.min(80, saturation)),
        lightness: Math.max(60, Math.min(72, lightness))
      };
    };

    uniqueSections.forEach((section, index) => {
      const color = generateUniqueColor(index);
      colorMap.set(section.uniqueId, {
        bg: `hsl(${color.hue}, ${color.saturation}%, ${color.lightness}%)`,
        border: `hsl(${color.hue}, ${color.saturation}%, ${
          color.lightness - 15
        }%)`,
        text: `hsl(${color.hue}, ${color.saturation}%, ${
          color.lightness - 30
        }%)`
      });
    });

    return colorMap;
  }, [structure, showZones]);

  const spaces3D = useMemo(() => {
    if (isFootprintStep) {
      return [];
    }
    return structure.flatMap((level) =>
      level.sections.flatMap((section) =>
        section.spaces.map((space) => ({
          id: space.uniqueId,
          spaceId: space.uniqueId,
          label: space.label,
          position: getSpace3DGridPosition(space),
          vertices: space.vertices,
          type: 'space' as const,
          itemId: space.itemId,
          sectionId: section.uniqueId,
          spacesInSection: section.spaces.length,
          sectionColor: sectionColors.get(section.uniqueId)
        }))
      )
    );
  }, [isFootprintStep, structure, sectionColors]);

  const structureSteps = useMemo(() => {
    return generateStructureAccuracyData();
  }, [
    structureAccuracySignature,
    JSON.stringify(projectSettings),
    structureTypeId,
    propertyLocation?.lat,
    propertyLocation?.lng,
    manuallyCompletedSteps,
    activeLevelIndex,
    activeSubStepIndex,
    modifiedDefaultCategories,
    defaultSizingItemsVersion,
    JSON.stringify(structureImageAssignments),
    JSON.stringify(structure.map((l) => hasFootprintPolygons(l))),
    existingSystems,
    totalBTUs.cooling
  ]);

  const handleRecenterRef = useRef<() => boolean>(() => false);
  const hasDoneInitialRecenterRef = useRef(false);
  const renderSidebarPanelContent = useCallback(
    (sectionId: string) => {
      if (sectionId === 'configure_level_details') {
        return <LevelDetailsAccordion />;
      }
      if (sectionId === 'confirm_details') {
        return <StructureDetailsStep />;
      }
      if (sectionId === 'footprint') {
        return (
          <div className='flex flex-col gap-3 p-4'>
            <div id='footprint-shapes'>
              <FootprintShapeSelector
                canAddShape={canAddFootprintShape}
                onAddShape={handleAddFootprintShape}
                onShapeSelect={() =>
                  setTimeout(() => handleRecenterRef.current(), 0)
                }
              />
            </div>
          </div>
        );
      }
      if (sectionId === 'sizing_tuning') {
        return (
          <div className='p-4'>
            <DefaultsStep />
          </div>
        );
      }
      if (sectionId === 'space_details') {
        return renderSpaceDetailsPanel(currentSpaceDetailsCategory);
      }
      if (sectionId === 'equipment_placement') {
        return (
          <div className='p-4'>
            <EquipmentPlacementList />
          </div>
        );
      }
      if (sectionId === 'add_spaces' || sectionId === 'condenser_locations') {
        return (
          <div className='p-4'>
            <SpaceList />
          </div>
        );
      }
      if (sectionId === 'orientation') {
        const orientationDescription = structureSteps.sections.find(
          (s) => s.id === 'orientation'
        )?.description;
        return (
          <div className='p-4'>
            <p className='text-base text-gray-600'>{orientationDescription}</p>
          </div>
        );
      }
      return null;
    },
    [
      currentSpaceDetailsCategory,
      renderSpaceDetailsPanel,
      structureSteps,
      canAddFootprintShape,
      handleAddFootprintShape
    ]
  );

  const sidebarSections: SidebarSectionType[] = useMemo(
    () =>
      structureSteps.sections.map((section) => ({
        ...section,
        renderPanelContent: () => renderSidebarPanelContent(section.id)
      })),
    [structureSteps.sections, renderSidebarPanelContent]
  );

  useEffect(() => {
    if (
      sidebarSections.length === 0 ||
      sidebarSections.some((section) => section.id === currentSectionId)
    ) {
      return;
    }
    const fallback =
      sidebarSections.find((section) => section.id === 'sizing_tuning') ??
      sidebarSections[sidebarSections.length - 1];
    setCurrentSectionId(fallback.id);
  }, [sidebarSections, currentSectionId, setCurrentSectionId]);

  const isAfterAddSpacesStep = useMemo(() => {
    const addSpacesSectionIndex = sidebarSections.findIndex(
      (s) => s.id === 'add_spaces'
    );
    const currentIndex = sidebarSections.findIndex(
      (s) => s.id === currentSectionId
    );
    return addSpacesSectionIndex !== -1 && currentIndex > addSpacesSectionIndex;
  }, [sidebarSections, currentSectionId]);

  useEffect(() => {
    if (activeTab !== 'structure' || !currentSectionId) {
      return;
    }
    dispatch(addVisitedStructureStep(currentSectionId));
  }, [activeTab, currentSectionId, dispatch]);

  useEffect(() => {
    if (activeItem !== null) {
      setAttributeModalOpen(true);
    }

    if (activeItem && !previousActiveItem && selectedSystems.length > 0) {
      warningModal(
        'Changing a space will reset all selected products and systems!'
      );
    }

    setPreviousActiveItem(activeItem);
  }, [activeItem, selectedSystems.length]);

  useEffect(() => {
    const currentActiveItem = activeItemRef.current;
    if (currentActiveItem) {
      const foundItem = findStructureItemById(
        structure,
        currentActiveItem.uniqueId
      );

      if (foundItem) {
        dispatch(
          setActiveItem({
            ...currentActiveItem,
            attributes: foundItem.attributes,
            sizingAttributes:
              'sizingAttributes' in foundItem
                ? foundItem.sizingAttributes
                : undefined
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, structureActiveItemAttributesSignature]);

  const getGridObjectsForLevelCallback = useCallback(
    (levelIndex: number): GridObjectType[] => {
      return getGridObjectsForLevel(levels, levelIndex, {
        electricalPanelsOnly: true,
        existingSystems
      });
    },
    [levels, existingSystems]
  );

  useEffect(() => {
    if (levels.length > 0 && selectedLevelIndex >= levels.length) {
      dispatch(setActiveLevelIndex(levels.length - 1));
    }
  }, [dispatch, levels.length, selectedLevelIndex]);

  const subStepDrivenKey = getSubStepDrivenKey(
    isAddSpacesStep,
    isSpaceDetailsStep,
    addSpacesNavigableLevels[addSpacesIndex]?.uniqueId,
    currentSpaceDetailsSubStep?.category,
    currentSpaceDetailsSubStep?.level.uniqueId
  );
  const prevSubStepDrivenKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!isAddSpacesStep && !isSpaceDetailsStep) {
      prevSubStepDrivenKeyRef.current = undefined;
      return;
    }
    if (!subStepDrivenKey) {
      return;
    }
    if (prevSubStepDrivenKeyRef.current === subStepDrivenKey) {
      return;
    }
    prevSubStepDrivenKeyRef.current = subStepDrivenKey;
    const subStepLevel = isAddSpacesStep
      ? addSpacesNavigableLevels[addSpacesIndex]
      : currentSpaceDetailsSubStep?.level;
    if (!subStepLevel) {
      return;
    }
    const levelIndex = structure.findIndex(
      (l) => l.uniqueId === subStepLevel.uniqueId
    );
    if (levelIndex !== -1) {
      dispatch(setActiveLevelIndex(levelIndex));
    }
  }, [
    isAddSpacesStep,
    isSpaceDetailsStep,
    addSpacesNavigableLevels,
    addSpacesIndex,
    currentSpaceDetailsSubStep,
    subStepDrivenKey,
    structure,
    dispatch
  ]);

  const prevSubStepSectionRef = useRef(currentSectionId);
  useEffect(() => {
    if (prevSubStepSectionRef.current !== currentSectionId) {
      dispatch(setActiveSubStepIndex(0));
    }
    prevSubStepSectionRef.current = currentSectionId;
  }, [currentSectionId, dispatch]);

  useEffect(() => {
    if (defaultLevelSectionRef.current === currentSectionId) {
      return;
    }
    if (structure.length === 0) {
      return;
    }
    defaultLevelSectionRef.current = currentSectionId;
    if (currentSectionId === 'configure_level_details') {
      dispatch(
        setActiveLevelDetailIndex(
          getFirstConditionedLevelIndexInList(getLevelDetailLevels(structure))
        )
      );
      return;
    }
    if (STEPS_WITH_OWN_LEVEL_DEFAULT.has(currentSectionId)) {
      return;
    }
    dispatch(setActiveLevelIndex(getDefaultConditionedLevelIndex(structure)));
  }, [currentSectionId, structure, dispatch]);

  useEffect(() => {
    if (currentSectionId === 'orientation') {
      markStructureStepCompleted('base_layout', 'orientation');
    }
    if (currentSectionId === 'space_details') {
      markStructureStepCompleted('space_details', 'space_details');
    }
  }, [currentSectionId, markStructureStepCompleted]);

  const prevSpaceDetailsCategoryRef = useRef(currentSpaceDetailsCategory);
  const hasAutoAddedFireplacesRef = useRef(false);

  useEffect(() => {
    const prev = prevSpaceDetailsCategoryRef.current;
    prevSpaceDetailsCategoryRef.current = currentSpaceDetailsCategory;

    if (!isSpaceDetailsStep) return;
    if (currentSpaceDetailsCategory !== 'appliances') return;
    if (prev === 'appliances') return;
    if (hasAutoAddedFireplacesRef.current) return;

    hasAutoAddedFireplacesRef.current = true;
    dispatch(autoAddFireplaceAppliances());
  }, [isSpaceDetailsStep, currentSpaceDetailsCategory, dispatch]);

  const getGridSpacesForLevelCallback = useCallback(
    (levelIndex: number) => {
      return getGridSpacesForLevel(levels, levelIndex);
    },
    [levels]
  );

  const getOverlaySpacesForLevelCallback = useCallback(
    (levelIndex: number) => {
      return getOverlaySpacesForLevel(levels, levelIndex);
    },
    [levels]
  );

  useEffect(() => {
    if (
      currentSectionId !== 'condenser_locations' &&
      currentSectionId !== 'orientation'
    ) {
      return;
    }
    setTimeout(() => handleRecenterRef.current(), 0);
  }, [currentSectionId]);

  const snapModifier: Modifier = useMemo(
    () =>
      ({ transform, activeNodeRect }) => {
        const gridRect = structureGridRef.current?.getBoundingClientRect();
        if (!gridRect || !activeNodeRect) {
          return snapTransformToGridWithZoom(
            transform,
            STRUCTURE_GRID_SIZE,
            gridZoom
          );
        }

        return snapTransformToGridWithZoomAndOrigin({
          transform,
          activeNodeRect,
          gridSize: STRUCTURE_GRID_SIZE,
          zoom: gridZoom,
          gridOrigin: {
            x: gridRect.left + gridPanOffset.x,
            y: gridRect.top + gridPanOffset.y
          }
        });
      },
    [gridZoom, gridPanOffset.x, gridPanOffset.y]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5
      }
    })
  );

  const { closeContextMenu } = useContextMenu();

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      closeContextMenu();
      setActiveId(event.active.id as string);
      setActiveDragData(event.active.data.current);

      const dragData = event.active.data.current;
      if (dragData?.type === 'grid-space') {
        const currentLevelSpaces =
          getGridSpacesForLevelCallback(selectedLevelIndex);
        const draggedSpaceId = (dragData.space as GridSpaceDataType)?.spaceId;

        if (
          selectedSpaceIds.size > 1 &&
          draggedSpaceId &&
          selectedSpaceIds.has(draggedSpaceId)
        ) {
          setIsMultiSelectSpaceDrag(true);
          const selectedSpaces = currentLevelSpaces.filter((space) =>
            selectedSpaceIds.has(space.spaceId)
          );
          setDraggedSectionSpaces(selectedSpaces);
        } else {
          setIsMultiSelectSpaceDrag(false);
          const draggedSpace = currentLevelSpaces.find(
            (space) => space.spaceId === draggedSpaceId
          );
          setDraggedSectionSpaces(draggedSpace ? [draggedSpace] : []);
        }
      } else {
        setIsMultiSelectSpaceDrag(false);
        setDraggedSectionSpaces([]);
      }
    },
    [
      selectedLevelIndex,
      getGridSpacesForLevelCallback,
      selectedSpaceIds,
      closeContextMenu
    ]
  );

  const getActiveDraggedItemCallback = useCallback(() => {
    return getActiveDraggedItem(activeId, activeDragData);
  }, [activeId, activeDragData]);

  const handleDragOver = useCallback(() => {}, []);

  const handleSpaceSelect = useCallback(
    (spaceId: string, isMultiSelect: boolean) => {
      setSelectedSpaceIds((prev) => {
        const newSet = new Set(prev);
        if (isMultiSelect) {
          if (newSet.has(spaceId)) {
            newSet.delete(spaceId);
          } else {
            newSet.add(spaceId);
          }
        } else {
          newSet.clear();
          newSet.add(spaceId);
        }
        return newSet;
      });
    },
    []
  );

  const handleMarqueeSelect = useCallback(
    (spaceIds: string[], isAdditive: boolean) => {
      setSelectedSpaceIds((prev) => {
        if (isAdditive) {
          const newSet = new Set(prev);
          spaceIds.forEach((id) => newSet.add(id));
          return newSet;
        }
        return new Set(spaceIds);
      });
    },
    []
  );

  const handleClearSelection = useCallback(() => {
    setSelectedSpaceIds(new Set());
  }, []);

  const handleMultiSpaceResize = useCallback(
    (updates: Map<string, PositionAndDimensionsType>) => {
      const levelPolygons = getEffectiveFootprintPolygons(
        levels[selectedLevelIndex]
      );
      if (
        Array.from(updates.values()).some(
          (position) => !isRectInAnyPolygon(position, levelPolygons)
        )
      ) {
        return;
      }

      dispatch(beginUndoBatch());
      updates.forEach((newPosition, spaceId) => {
        const roundedLength = Math.max(
          MIN_SPACE_DIM_FEET,
          Math.round(newPosition.height)
        );
        const roundedWidth = Math.max(
          MIN_SPACE_DIM_FEET,
          Math.round(newPosition.width)
        );

        const updatedSpace: PartialWithRequired<SpaceType, 'uniqueId'> = {
          uniqueId: spaceId,
          position: {
            x: newPosition.x,
            y: newPosition.y
          },
          length: roundedLength,
          width: roundedWidth
        };

        dispatch(updateStructureItem(updatedSpace));
        dispatch(
          updateStructureItemAttribute({
            uniqueId: spaceId,
            attributeCode: 'square_footage',
            value: Math.round(roundedLength * roundedWidth)
          })
        );
      });
      dispatch(endUndoBatch());
    },
    [dispatch, levels, selectedLevelIndex]
  );

  const handleZoomChange = useCallback((newZoom: number) => {
    setGridZoom(newZoom);
    setShowRecenterButton(true);
  }, []);

  const handlePanOffsetChange = useCallback(
    (offset: { x: number; y: number }) => {
      setGridPanOffset(offset);
      setShowRecenterButton(true);
    },
    []
  );

  const imageAutoScaleGenerationRef = useRef(0);

  const autoScaleLevelImagesToSqft = useCallback(async () => {
    const generation = imageAutoScaleGenerationRef.current;
    const sqftSetting = projectSettings.find(
      (ps) => ps.code === 'square_footage'
    );
    const totalSqftFromSetting = Number(sqftSetting?.value);
    const normalLevelCount = structure.filter((l) => l.type === 'level').length;
    if (
      !totalSqftFromSetting ||
      totalSqftFromSetting <= 0 ||
      normalLevelCount <= 0
    ) {
      return;
    }

    const levelImageAttachments = Object.entries(structureImageAssignments)
      .filter(([, a]) => a?.structureItemTypeCode === 'level')
      .map(([id, a]) => ({ attachmentId: Number(id), assignment: a! }));

    if (levelImageAttachments.length === 0) return;

    const stacked = await resolveStackedLevelImageTransforms({
      assignments: structureImageAssignmentsRef.current,
      structureAttachments: structureAttachmentsRef.current,
      totalSqft: totalSqftFromSetting,
      levelCount: normalLevelCount,
      cachedDimsByAttachmentId:
        currentLayoutImageAttachmentId !== null && layoutImageDimsRef.current
          ? {
              [currentLayoutImageAttachmentId]: layoutImageDimsRef.current
            }
          : undefined
    });

    if (imageAutoScaleGenerationRef.current !== generation) return;

    const validUpdates = stacked.filter((entry) => {
      const latestAssignment =
        structureImageAssignmentsRef.current[entry.attachmentId];
      return latestAssignment != null;
    });

    if (validUpdates.length === 0) return;

    dispatch(beginUndoBatch());
    try {
      validUpdates.forEach(({ attachmentId, transform }) => {
        dispatch(
          updateStructureImageTransform({
            attachmentId,
            ...transform
          })
        );
      });
    } finally {
      dispatch(endUndoBatch());
    }

    validUpdates.forEach(({ attachmentId, transform }) => {
      updateImageTransform({ attachmentId, ...transform })
        .unwrap()
        .catch((error) =>
          console.error('Failed to save image transform:', error)
        );
    });

    setFootprintImageRecenterNonce((n) => n + 1);
  }, [
    projectSettings,
    structure,
    structureImageAssignments,
    structureAttachments,
    currentLayoutImageAttachmentId,
    dispatch,
    updateImageTransform
  ]);

  // Tonnage conflict dialog (AHA-6681 tonnage ruling 5, mock S4): the
  // arbiter for a calc that maps outside the current tonnage selection.
  // Armed only on the LAST section's Next — the forward hop off L&S — and
  // only on the strict ducted-only preference. Tab clicks that bypass Next
  // are deliberately NOT intercepted (the Choose Systems chip's passive
  // review affix opens the same dialog there).
  const structureTonnage = useStructureTonnage();
  const tonnageConflictOpenState = useState(false);
  const [, setTonnageConflictOpen] = tonnageConflictOpenState;
  const isDuctedOnlyPref = useMemo(
    () =>
      isDuctedOnlySystemPreference(
        projectSettings.find((ps) => ps.code === 'system_preference')?.value
      ),
    [projectSettings]
  );
  // System Design is hidden on the bypass modes (ducted-only, or hybrid with
  // no mini-split rooms — utils/system-design-bypass): the configurations
  // page offers nothing there — L&S Next lands on Choose Systems.
  const systemDesignLocked = isSystemDesignLocked(useSystemDesignBypassMode());
  const systemPreferenceValue = projectSettings.find(
    (ps) => ps.code === 'system_preference'
  )?.value;
  const structurePreviousTab: ActiveTabType = structureBackTab(
    existingEquipmentPageVisible(
      systemPreferenceValue,
      projectSettings.find((ps) => ps.code === 'project_type')?.value
    ),
    equipmentPreferencesPageVisible(systemPreferenceValue)
  );
  const structureNextTab: ActiveTabType = systemDesignLocked
    ? 'chooseSystems'
    : 'configurations';
  const tonnageConflictArmed =
    isDuctedOnlyPref &&
    structureTonnage.loaded &&
    structureTonnage.system !== null &&
    shouldOpenTonnageConflict({
      effective: structureTonnage.effective,
      calc: structureTonnage.calc,
      record: structureTonnage.record
    });
  const isSimpleSizingStep = useIsSimpleSizingPanel();
  const handleDetailedEstimateOptIn = useCallback(() => {
    dispatch(setProjectSettingValue('sizing_preference', 'Detailed Estimate'));
    dispatch(setActiveStructureStepId('configure_level_details'));
  }, [dispatch]);
  const detailedEstimateButton = isSimpleSizingStep
    ? {
        text: 'Get a more detailed sizing estimate',
        onClick: handleDetailedEstimateOptIn,
        tooltip:
          'Map out your floor plan for a room-by-room load calculation with the full sizing calculator.'
      }
    : undefined;
  const hideBasicInfoProgressBar = shouldHideBasicInfoProgressBar(
    isSimpleSizingStep,
    sidebarSections
  );
  const isLastSidebarSection =
    sidebarSections.length > 0 &&
    currentSectionId === sidebarSections[sidebarSections.length - 1].id;

  const stepSidebarOnNext = useMemo(() => {
    const adoptCalcForUnsure = () => {
      if (
        !isUnsureTonnage(structureTonnage.record) ||
        !structureTonnage.system ||
        !structureTonnage.calc
      ) {
        return;
      }
      const tonnage = adoptCalcTonnageRecord(
        structureTonnage.record,
        structureTonnage.calc
      );
      dispatch(
        setSystemTonnageRecord({
          systemUniqueId: structureTonnage.system.uniqueId,
          tonnage
        })
      );
      persistTonnageRecord(
        structureTonnage.system.uniqueId,
        structureTonnage.entry,
        tonnage
      );
    };
    return (): boolean | undefined => {
      if (isSimpleSizingStep) {
        adoptCalcForUnsure();
        dispatch(setActiveTab(structureNextTab));
        return false;
      }
      if (!isLastSidebarSection) return undefined;
      if (tonnageConflictArmed) {
        setTonnageConflictOpen(true);
        return false;
      }
      adoptCalcForUnsure();
      return undefined;
    };
  }, [
    isSimpleSizingStep,
    isLastSidebarSection,
    structureTonnage,
    structureNextTab,
    dispatch,
    tonnageConflictArmed,
    setTonnageConflictOpen
  ]);

  // Dialog resolution: mirror + persist the record (resolvedCalcTons stamps
  // the no-re-ask guard), then complete the navigation the intercepted Next
  // was about to make.
  const handleTonnageConflictResolve = useCallback(
    (record: TonnageRecord) => {
      const { system, entry } = structureTonnage;
      if (!system) return;
      dispatch(
        setSystemTonnageRecord({
          systemUniqueId: system.uniqueId,
          tonnage: record
        })
      );
      persistTonnageRecord(system.uniqueId, entry, record);
      setTonnageConflictOpen(false);
      dispatch(setActiveTab(structureNextTab));
    },
    [structureTonnage, dispatch, setTonnageConflictOpen, structureNextTab]
  );

  const totalSqft = useMemo(() => {
    const setting = projectSettings.find((ps) => ps.code === 'square_footage');
    const value = Number(setting?.value);
    return Number.isNaN(value) ? 0 : value;
  }, [projectSettings]);

  const totalFootprintArea = useMemo(
    () => getTotalFootprintArea(structure),
    [structure]
  );

  const firstMainLevelIndex = useMemo(
    () => getFirstMainLevelIndex(structure),
    [structure]
  );

  const isOnFirstMainLevel =
    structure.length > 0 && selectedLevelIndex === firstMainLevelIndex;

  const autoScaleRatio = useMemo(
    () =>
      computeAutoScaleParams({
        conditionedAreas: [totalFootprintArea],
        totalSqft
      }).ratio,
    [totalFootprintArea, totalSqft]
  );

  const autoScaleVisibilityPivot = useMemo(
    () =>
      effectiveFootprintPolygons.length > 0
        ? resolveAutoScalePivot({
            footprintPolygons: effectiveFootprintPolygons,
            layoutImageAttachmentId: currentLayoutImageAttachmentId,
            imageTransform: layoutImageTransformForCurrentLevel,
            imageDims: layoutImageDimsRef.current
          })
        : null,
    [
      effectiveFootprintPolygons,
      currentLayoutImageAttachmentId,
      layoutImageTransformForCurrentLevel
    ]
  );

  const wouldAutoScaleHaveEffect = useMemo(() => {
    if (!autoScaleVisibilityPivot) return false;
    return wouldAutoScaleChangeAnyFootprint({
      levels: structure,
      ratio: autoScaleRatio,
      pivot: autoScaleVisibilityPivot
    });
  }, [structure, autoScaleRatio, autoScaleVisibilityPivot]);

  const canAutoScaleFootprint =
    effectiveFootprintPolygons.length > 0 &&
    shouldShowAutoScaleForArea(totalFootprintArea, totalSqft) &&
    wouldAutoScaleHaveEffect;

  const handleAutoScaleFootprint = useCallback(async () => {
    imageAutoScaleGenerationRef.current += 1;

    if (
      !activeLevel ||
      effectiveFootprintPolygons.length === 0 ||
      totalSqft <= 0 ||
      totalFootprintArea <= 0
    ) {
      return;
    }

    const { ratio } = computeAutoScaleParams({
      conditionedAreas: [totalFootprintArea],
      totalSqft
    });

    if (ratio === 1) return;

    const pivot = resolveAutoScalePivot({
      footprintPolygons: effectiveFootprintPolygons,
      layoutImageAttachmentId: currentLayoutImageAttachmentId,
      imageTransform: layoutImageTransformForCurrentLevel,
      imageDims: layoutImageDimsRef.current
    });

    const levelImageAttachments = Object.entries(structureImageAssignments)
      .filter(([, a]) => a?.structureItemTypeCode === 'level')
      .map(([id, a]) => ({ attachmentId: Number(id), assignment: a! }));

    const imageUpdates = await Promise.all(
      levelImageAttachments.map(async ({ attachmentId, assignment }) => {
        const url = structureAttachments.find(
          (a) => a.id === attachmentId
        )?.url;
        if (!url) return null;
        const cached =
          attachmentId === currentLayoutImageAttachmentId
            ? layoutImageDimsRef.current
            : null;
        const dims = cached ?? (await loadImageDimensions(url));
        if (!dims) return null;
        const nextTransform = computeScaledImageTransformAroundPivot(
          assignment,
          dims,
          pivot,
          ratio
        );
        return { attachmentId, nextTransform };
      })
    );

    dispatch(beginUndoBatch());
    try {
      dispatch(scaleAllLevelFootprints({ ratio, center: pivot }));
      dispatch(scaleAllSpacesWithImage({ ratio, center: pivot, dx: 0, dy: 0 }));

      imageUpdates
        .filter((u): u is NonNullable<typeof u> => u !== null)
        .forEach(({ attachmentId, nextTransform }) => {
          dispatch(
            updateStructureImageTransform({
              attachmentId,
              ...nextTransform
            })
          );
          updateImageTransform({ attachmentId, ...nextTransform })
            .unwrap()
            .catch((error) =>
              console.error('Failed to save image transform:', error)
            );
        });
    } finally {
      dispatch(endUndoBatch());
    }
  }, [
    activeLevel,
    effectiveFootprintPolygons,
    totalSqft,
    totalFootprintArea,
    currentLayoutImageAttachmentId,
    layoutImageTransformForCurrentLevel,
    structureImageAssignments,
    structureAttachments,
    dispatch,
    updateImageTransform
  ]);

  const hasAnyFootprint = structure.some((level) =>
    hasFootprintPolygons(level)
  );
  const activeFootprintMode = resolveActiveFootprintMode(
    isFootprintStep,
    footprintMode
  );
  const allowImageInteraction = activeFootprintMode === 'image' || isImagesStep;
  const footprintScaleRatio =
    activeFootprintMode === 'scale' && localFootprintTransform
      ? localFootprintTransform.ratio
      : 1;
  const footprintDashboardArea = getScaledFootprintArea(
    totalFootprintArea,
    footprintScaleRatio
  );
  const metricsFootprintMode = isFootprintStep
    ? {
        area: footprintDashboardArea,
        onAutoScale: shouldShowAutoScaleForArea(
          footprintDashboardArea,
          totalSqft
        )
          ? handleAutoScaleFootprint
          : undefined,
        immediate: footprintScaleRatio !== 1
      }
    : undefined;
  const effectiveFootprintPolygonsRef = useRef(effectiveFootprintPolygons);
  effectiveFootprintPolygonsRef.current = effectiveFootprintPolygons;
  const structureRef = useRef(structure);
  structureRef.current = structure;

  useEffect(() => {
    if (!allowImageInteraction) {
      setIsLayoutImageSelected(false);
    }
  }, [allowImageInteraction]);

  useEffect(() => {
    if (activeFootprintMode === 'image') {
      setIsLayoutImageSelected(true);
      setLayoutUnderlayOpacity((prev) => Math.max(prev, 50));
    }
  }, [activeFootprintMode]);

  useEffect(() => {
    if (activeFootprintMode === 'scale') {
      setIsLayoutImageSelected(false);
    }
  }, [activeFootprintMode]);

  useEffect(() => {
    if (!isFootprintStep) {
      setFootprintMode('draw');
      footprintRecenterDoneKeyRef.current = null;
    }
  }, [isFootprintStep]);

  useEffect(() => {
    if (
      isFootprintStep &&
      footprintMode === 'image' &&
      currentLayoutImageAttachmentId === null
    ) {
      setFootprintMode('draw');
      setIsLayoutImageSelected(false);
    }
  }, [isFootprintStep, footprintMode, currentLayoutImageAttachmentId]);

  const hasInitializedImageModeLevelRef = useRef(false);

  useEffect(() => {
    if (!isFootprintStep) {
      hasInitializedImageModeLevelRef.current = false;
    }
  }, [isFootprintStep]);

  const hasInitializedFootprintLevelRef = useRef(false);

  useEffect(() => {
    if (!isFootprintStep) {
      hasInitializedFootprintLevelRef.current = false;
      return;
    }
    if (hasInitializedFootprintLevelRef.current) {
      return;
    }
    if (structure.length === 0) {
      return;
    }
    if (firstMainLevelIndex !== selectedLevelIndex) {
      dispatch(setActiveLevelIndex(firstMainLevelIndex));
    }
    hasInitializedFootprintLevelRef.current = true;
  }, [
    isFootprintStep,
    structure,
    selectedLevelIndex,
    firstMainLevelIndex,
    dispatch
  ]);

  const hasAutoScaledImagesOnEntryRef = useRef(false);
  const hasAutoScaledFootprintOnEntryRef = useRef(false);

  useEffect(() => {
    hasAutoScaledFootprintOnEntryRef.current = false;
  }, [structureUniqueId]);

  useEffect(() => {
    if (!isFootprintStep) {
      if (hasAutoScaledImagesOnEntryRef.current) {
        imageAutoScaleGenerationRef.current += 1;
        hasAutoScaledImagesOnEntryRef.current = false;
      }
      return;
    }

    const hasLevelLayoutImages = Object.values(structureImageAssignments).some(
      (a) => a?.structureItemTypeCode === 'level'
    );
    const action = resolveFootprintStepEntryScaleAction({
      isFootprintStep,
      alreadyRanFootprint: hasAutoScaledFootprintOnEntryRef.current,
      alreadyRanImages: hasAutoScaledImagesOnEntryRef.current,
      canAutoScaleFootprint,
      isOnFirstMainLevel,
      hasLevelLayoutImages,
      totalSqft
    });

    if (action === 'scale-footprint') {
      hasAutoScaledFootprintOnEntryRef.current = true;
      hasAutoScaledImagesOnEntryRef.current = true;
      handleAutoScaleFootprint();
      return;
    }

    if (action === 'scale-images') {
      hasAutoScaledImagesOnEntryRef.current = true;
      autoScaleLevelImagesToSqft();
    }
  }, [
    isFootprintStep,
    structureImageAssignments,
    totalSqft,
    canAutoScaleFootprint,
    isOnFirstMainLevel,
    handleAutoScaleFootprint,
    autoScaleLevelImagesToSqft
  ]);

  const effectivePreviewTransform = useMemo(() => {
    if (activeFootprintMode === 'scale' && localFootprintTransform) {
      return localFootprintTransform;
    }
    return null;
  }, [activeFootprintMode, localFootprintTransform]);

  const displayFootprintPolygons = useMemo(() => {
    if (!effectivePreviewTransform || !footprintBeforeDragRef.current) {
      return effectiveFootprintPolygons;
    }
    return transformFootprintPolygons(
      footprintBeforeDragRef.current,
      effectivePreviewTransform
    );
  }, [effectivePreviewTransform, effectiveFootprintPolygons]);

  const displayGridSpaces = useMemo(() => {
    if (isFootprintStep) {
      return [];
    }
    const baseSpaces = getGridSpacesForLevelCallback(selectedLevelIndex);
    if (!effectivePreviewTransform || !spacesBeforeDragRef.current) {
      return baseSpaces;
    }
    const { ratio, dx, dy, center } = effectivePreviewTransform;
    return baseSpaces.map((gs) => {
      const original = spacesBeforeDragRef.current?.get(gs.spaceId);
      if (!original) return gs;
      const scaled = scaleSpaceGeometry(original, ratio, center, dx, dy);
      if (!scaled) return gs;
      return gridSpaceFromScaledSpace(gs, scaled);
    });
  }, [
    isFootprintStep,
    getGridSpacesForLevelCallback,
    selectedLevelIndex,
    effectivePreviewTransform
  ]);

  const displayImageTransform = useMemo(() => {
    if (
      activeFootprintMode !== 'scale' ||
      !localFootprintTransform ||
      !layoutImageDimsRef.current ||
      imageScaleBeforeDragRef.current === null ||
      imagePositionBeforeDragRef.current === null ||
      !layoutImageTransformForCurrentLevel
    ) {
      return layoutImageTransformForCurrentLevel;
    }

    const { ratio, dx, dy, center } = localFootprintTransform;
    const dims = layoutImageDimsRef.current;
    const oldPos = imagePositionBeforeDragRef.current;
    const oldScale = imageScaleBeforeDragRef.current;

    const oldVisualCenterX = oldPos.x + dims.naturalWidth / 2;
    const oldVisualCenterY = oldPos.y + dims.naturalHeight / 2;
    const centerPxX = center.x * STRUCTURE_GRID_SIZE;
    const centerPxY = center.y * STRUCTURE_GRID_SIZE;
    const dxPx = dx * STRUCTURE_GRID_SIZE;
    const dyPx = dy * STRUCTURE_GRID_SIZE;

    const newVisualCenterX =
      (oldVisualCenterX - centerPxX) * ratio + centerPxX + dxPx;
    const newVisualCenterY =
      (oldVisualCenterY - centerPxY) * ratio + centerPxY + dyPx;

    return {
      ...layoutImageTransformForCurrentLevel,
      scale: oldScale * ratio,
      startPositionX: newVisualCenterX - dims.naturalWidth / 2,
      startPositionY: newVisualCenterY - dims.naturalHeight / 2
    };
  }, [
    activeFootprintMode,
    localFootprintTransform,
    layoutImageTransformForCurrentLevel
  ]);

  const handleRecenter = useCallback((): boolean => {
    const container = structureGridRef.current;
    if (!container) return false;
    if (container.clientWidth === 0 || container.clientHeight === 0) {
      return false;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    if (effectiveFootprintPolygons.length > 0) {
      for (const point of effectiveFootprintPolygons.flat()) {
        const px = point.x * STRUCTURE_GRID_SIZE;
        const py = point.y * STRUCTURE_GRID_SIZE;
        minX = Math.min(minX, px);
        minY = Math.min(minY, py);
        maxX = Math.max(maxX, px);
        maxY = Math.max(maxY, py);
      }
    } else {
      let spaces = getGridSpacesForLevelCallback(selectedLevelIndex);

      if (spaces.length === 0) {
        const isNearStart = selectedLevelIndex <= levels.length / 2;
        const searchOrder = isNearStart ? [1, -1] : [-1, 1];

        for (const direction of searchOrder) {
          for (
            let i = selectedLevelIndex + direction;
            i >= 0 && i < levels.length;
            i += direction
          ) {
            const fallbackSpaces = getGridSpacesForLevelCallback(i);
            if (fallbackSpaces.length > 0) {
              spaces = fallbackSpaces;
              break;
            }
          }
          if (spaces.length > 0) break;
        }
      }

      if (spaces.length > 0) {
        for (const space of spaces) {
          minX = Math.min(minX, space.position.x);
          minY = Math.min(minY, space.position.y);
          maxX = Math.max(maxX, space.position.x + space.position.width);
          maxY = Math.max(maxY, space.position.y + space.position.height);
        }
      } else if (
        layoutImageForCurrentLevel &&
        layoutImageTransformForCurrentLevel &&
        layoutImageDimsRef.current
      ) {
        const imgScale = layoutImageTransformForCurrentLevel.scale ?? 1;
        const posX = layoutImageTransformForCurrentLevel.startPositionX ?? 0;
        const posY = layoutImageTransformForCurrentLevel.startPositionY ?? 0;
        const { naturalWidth, naturalHeight } = layoutImageDimsRef.current;
        const cx = posX + naturalWidth / 2;
        const cy = posY + naturalHeight / 2;
        const halfW = (naturalWidth * imgScale) / 2;
        const halfH = (naturalHeight * imgScale) / 2;
        minX = cx - halfW;
        minY = cy - halfH;
        maxX = cx + halfW;
        maxY = cy + halfH;
      } else {
        return false;
      }
    }

    const view = computeZoomToFitView(
      { minX, minY, maxX, maxY },
      { width: container.clientWidth, height: container.clientHeight }
    );

    setGridZoom(view.zoom);
    setGridPanOffset({ x: view.panX, y: view.panY });
    setShowRecenterButton(false);
    return true;
  }, [
    getGridSpacesForLevelCallback,
    selectedLevelIndex,
    levels.length,
    effectiveFootprintPolygons,
    layoutImageForCurrentLevel,
    layoutImageTransformForCurrentLevel
  ]);

  handleRecenterRef.current = handleRecenter;

  useKeyShortcuts({
    enabled: isOnLayoutSection && !show3DView,
    bindings: { [RECENTER_VIEW_SHORTCUT_KEY]: handleRecenter }
  });

  useAddSpacesInitialRecenter({
    isAddSpacesStep,
    show3DView,
    selectedLevelIndex,
    addSpacesIndex,
    structureUniqueId,
    structureGridRef,
    handleRecenterRef
  });

  useEffect(() => {
    if (show3DView) return;
    const container = structureGridRef.current;
    if (!container) {
      handleRecenterRef.current();
      return;
    }
    const spaces = getGridSpacesForLevelCallback(selectedLevelIndex);
    if (
      spaces.length > 0 &&
      !areSpacesInViewport(
        spaces,
        container.clientWidth,
        container.clientHeight,
        gridZoomRef.current,
        gridPanOffsetRef.current
      )
    ) {
      handleRecenterRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show3DView, currentSectionId, selectedLevelIndex]);

  useEffect(() => {
    hasDoneInitialRecenterRef.current = false;
  }, [structureUniqueId]);

  useEffect(() => {
    if (show3DView) return undefined;
    if (hasDoneInitialRecenterRef.current) return undefined;
    if (structure.length === 0) return undefined;

    const timeoutId = setTimeout(() => {
      const container = structureGridRef.current;
      if (!container) return;
      if (container.clientWidth === 0 || container.clientHeight === 0) return;

      const spaces = getGridSpacesForLevelCallback(selectedLevelIndex);
      const hasFootprint = effectiveFootprintPolygons.length > 0;
      const hasImage = !!layoutImageForCurrentLevel;

      if (spaces.length === 0 && !hasFootprint && !hasImage) return;

      hasDoneInitialRecenterRef.current = true;
      handleRecenterRef.current();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [
    show3DView,
    structure,
    selectedLevelIndex,
    currentSectionId,
    effectiveFootprintPolygons,
    layoutImageForCurrentLevel,
    getGridSpacesForLevelCallback
  ]);

  const footprintRecenterRequestIdRef = useRef(0);

  const footprintRecenterKey = `${structureUniqueId}:${selectedLevelIndex}:${
    currentLayoutImageAttachmentId ?? 'none'
  }:${footprintImageRecenterNonce}`;

  const recenterOnLayoutImage = useCallback(
    async (requestId: number, doneKey: string) => {
      const isStale = () => requestId !== footprintRecenterRequestIdRef.current;
      const markDoneIfRecentered = (recentered: boolean) => {
        if (recentered) {
          footprintRecenterDoneKeyRef.current = doneKey;
        }
      };

      const container = await waitForElementSize(
        () => structureGridRef.current,
        isStale
      );
      if (!container || isStale()) return;

      if (!layoutImageForCurrentLevel || !layoutImageTransformForCurrentLevel) {
        markDoneIfRecentered(handleRecenterRef.current());
        return;
      }

      const dims = await loadImageDimensions(layoutImageForCurrentLevel);
      if (isStale()) return;

      const bounds = imagePixelBounds(
        layoutImageTransformForCurrentLevel,
        dims
      );
      if (!bounds) {
        markDoneIfRecentered(handleRecenterRef.current());
        return;
      }

      const view = computeZoomToFitView(bounds, {
        width: container.clientWidth,
        height: container.clientHeight
      });
      setGridZoom(view.zoom);
      setGridPanOffset({ x: view.panX, y: view.panY });
      setShowRecenterButton(false);
      footprintRecenterDoneKeyRef.current = doneKey;
    },
    [
      layoutImageForCurrentLevel,
      layoutImageTransformForCurrentLevel,
      setGridZoom,
      setGridPanOffset
    ]
  );

  const recenterOnLayoutImageRef = useRef(recenterOnLayoutImage);
  recenterOnLayoutImageRef.current = recenterOnLayoutImage;

  useEffect(() => {
    if (!isFootprintStep || show3DView) return undefined;
    if (footprintRecenterDoneKeyRef.current === footprintRecenterKey) {
      return undefined;
    }

    footprintRecenterRequestIdRef.current += 1;
    const requestId = footprintRecenterRequestIdRef.current;
    const doneKey = footprintRecenterKey;
    const timeoutId = setTimeout(() => {
      recenterOnLayoutImageRef.current(requestId, doneKey);
    }, 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isFootprintStep,
    show3DView,
    footprintRecenterKey,
    layoutImageForCurrentLevel,
    layoutImageTransformForCurrentLevel,
    structure.length,
    structureAttachments.length
  ]);

  const handleLayoutImageTransformChange = useCallback(
    (transform: StructureImageTransformType) => {
      setLocalImageTransform((prev) => {
        const base = prev ?? {
          scale: layoutImageTransformForCurrentLevel?.scale ?? 1,
          rotation: layoutImageTransformForCurrentLevel?.rotation ?? 0,
          startPositionX:
            layoutImageTransformForCurrentLevel?.startPositionX ?? 0,
          startPositionY:
            layoutImageTransformForCurrentLevel?.startPositionY ?? 0
        };
        return {
          ...base,
          ...transform
        };
      });
    },
    [layoutImageTransformForCurrentLevel]
  );

  const handleLayoutImageTransformEnd = useCallback(
    async (overrideTransform?: StructureImageTransformType) => {
      const transform = overrideTransform ?? localImageTransform;
      if (!currentLayoutImageAttachmentId || !transform) {
        setLocalImageTransform(null);
        return;
      }

      dispatch(
        updateStructureImageTransform({
          attachmentId: currentLayoutImageAttachmentId,
          scale: transform.scale,
          rotation: transform.rotation,
          startPositionX: transform.startPositionX,
          startPositionY: transform.startPositionY
        })
      );

      try {
        await updateImageTransform({
          attachmentId: currentLayoutImageAttachmentId,
          scale: transform.scale,
          rotation: transform.rotation,
          startPositionX: transform.startPositionX,
          startPositionY: transform.startPositionY
        });
      } catch (error) {
        console.error('Failed to save image transform:', error);
      }

      setLocalImageTransform(null);
    },
    [
      currentLayoutImageAttachmentId,
      localImageTransform,
      dispatch,
      updateImageTransform
    ]
  );

  const clearScaleDragRefs = useCallback(() => {
    footprintBeforeDragRef.current = null;
    spacesBeforeDragRef.current = null;
    imageScaleBeforeDragRef.current = null;
    imagePositionBeforeDragRef.current = null;
  }, []);

  const handleFootprintScaleTransformChange = useCallback(
    (transform: FootprintScalingTransform) => {
      setLocalFootprintTransform((prev) => {
        if (prev === null) {
          if (effectiveFootprintPolygonsRef.current.length > 0) {
            footprintBeforeDragRef.current =
              effectiveFootprintPolygonsRef.current.map((polygon) => [
                ...polygon
              ]);
          }
          spacesBeforeDragRef.current = snapshotSpaceGeometries(
            structureRef.current
          );
          if (layoutImageTransformForCurrentLevel) {
            imageScaleBeforeDragRef.current =
              layoutImageTransformForCurrentLevel.scale ?? 1;
            imagePositionBeforeDragRef.current = {
              x: layoutImageTransformForCurrentLevel.startPositionX ?? 0,
              y: layoutImageTransformForCurrentLevel.startPositionY ?? 0
            };
          }
        }
        return transform;
      });
    },
    [layoutImageTransformForCurrentLevel]
  );

  const handleFootprintScaleTransformEnd = useCallback(async () => {
    const transform = localFootprintTransform;
    if (
      !transform ||
      !footprintBeforeDragRef.current ||
      !activeLevel ||
      (transform.ratio === 1 && transform.dx === 0 && transform.dy === 0)
    ) {
      clearScaleDragRefs();
      setLocalFootprintTransform(null);
      return;
    }

    const { ratio, dx, dy, center } = transform;

    const shouldCommitImage =
      currentLayoutImageAttachmentId !== null &&
      imageScaleBeforeDragRef.current !== null &&
      imagePositionBeforeDragRef.current !== null &&
      layoutImageDimsRef.current !== null &&
      layoutImageTransformForCurrentLevel !== undefined;

    let nextImageTransform: StructureImageTransformType | null = null;
    if (
      shouldCommitImage &&
      imageScaleBeforeDragRef.current !== null &&
      imagePositionBeforeDragRef.current !== null &&
      layoutImageDimsRef.current !== null
    ) {
      const dims = layoutImageDimsRef.current;
      const oldPos = imagePositionBeforeDragRef.current;
      const oldScale = imageScaleBeforeDragRef.current;
      const oldVisualCenterX = oldPos.x + dims.naturalWidth / 2;
      const oldVisualCenterY = oldPos.y + dims.naturalHeight / 2;
      const centerPxX = center.x * STRUCTURE_GRID_SIZE;
      const centerPxY = center.y * STRUCTURE_GRID_SIZE;
      const dxPx = dx * STRUCTURE_GRID_SIZE;
      const dyPx = dy * STRUCTURE_GRID_SIZE;
      const newVisualCenterX =
        (oldVisualCenterX - centerPxX) * ratio + centerPxX + dxPx;
      const newVisualCenterY =
        (oldVisualCenterY - centerPxY) * ratio + centerPxY + dyPx;
      nextImageTransform = {
        scale: oldScale * ratio,
        rotation: layoutImageTransformForCurrentLevel?.rotation ?? 0,
        startPositionX: newVisualCenterX - dims.naturalWidth / 2,
        startPositionY: newVisualCenterY - dims.naturalHeight / 2
      };
    }

    dispatch(beginUndoBatch());
    try {
      dispatch(scaleAllLevelFootprints({ ratio, center, dx, dy }));
      dispatch(scaleAllSpacesWithImage({ ratio, center, dx, dy }));

      if (nextImageTransform && currentLayoutImageAttachmentId !== null) {
        dispatch(
          updateStructureImageTransform({
            attachmentId: currentLayoutImageAttachmentId,
            ...nextImageTransform
          })
        );
      }
    } finally {
      dispatch(endUndoBatch());
    }

    if (nextImageTransform && currentLayoutImageAttachmentId !== null) {
      try {
        await updateImageTransform({
          attachmentId: currentLayoutImageAttachmentId,
          ...nextImageTransform
        }).unwrap();
      } catch (error) {
        console.error('Failed to save image transform:', error);
      }
    }

    clearScaleDragRefs();
    setLocalFootprintTransform(null);
  }, [
    localFootprintTransform,
    activeLevel,
    dispatch,
    currentLayoutImageAttachmentId,
    layoutImageTransformForCurrentLevel,
    updateImageTransform,
    clearScaleDragRefs
  ]);

  const handleLayoutImageLoad = useCallback(
    async (dims: { naturalWidth: number; naturalHeight: number }) => {
      layoutImageDimsRef.current = dims;

      if (!currentLayoutImageAttachmentId) return;

      if (
        isFootprintStep &&
        footprintRecenterDoneKeyRef.current !== footprintRecenterKey
      ) {
        setFootprintImageRecenterNonce((n) => n + 1);
      }

      const sqftSetting = projectSettings.find(
        (ps) => ps.code === 'square_footage'
      );
      const totalSqftFromSetting = Number(sqftSetting?.value);
      const conditionedLevelCount = getConditionedLevelCount(structure);

      const stacked = await resolveStackedLevelImageTransforms({
        assignments: structureImageAssignmentsRef.current,
        structureAttachments: structureAttachmentsRef.current,
        totalSqft: totalSqftFromSetting,
        levelCount: conditionedLevelCount,
        cachedDimsByAttachmentId: {
          [currentLayoutImageAttachmentId]: dims
        }
      });

      const stackedForCurrent = stacked.find(
        (entry) => entry.attachmentId === currentLayoutImageAttachmentId
      );
      if (!stackedForCurrent) return;

      handleLayoutImageTransformChange(stackedForCurrent.transform);
      handleLayoutImageTransformEnd(stackedForCurrent.transform);
      setFootprintImageRecenterNonce((n) => n + 1);
    },
    [
      currentLayoutImageAttachmentId,
      projectSettings,
      structure,
      isFootprintStep,
      footprintRecenterKey,
      handleLayoutImageTransformChange,
      handleLayoutImageTransformEnd
    ]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      createHandleDragEnd({
        event,
        dispatch,
        structure,
        levels,
        selectedLevelIndex,
        configurations,
        activeConfigIndex: activeConfig,
        selectedSystems,
        draggedSectionSpaces,
        isMultiSelectSpaceDrag,
        getGridSpacesForLevel: getGridSpacesForLevelCallback,
        setActiveId,
        setActiveDragData,
        setDraggedSectionSpaces,
        structureOrientation,
        zoom: gridZoom,
        panOffset: gridPanOffset,
        autoResizeSpacesToFit,
        footprintPolygons: effectiveFootprintPolygons
      });
      setIsMultiSelectSpaceDrag(false);
    },
    [
      dispatch,
      configurations,
      activeConfig,
      selectedSystems,
      levels,
      selectedLevelIndex,
      draggedSectionSpaces,
      isMultiSelectSpaceDrag,
      getGridSpacesForLevelCallback,
      structure,
      structureOrientation,
      gridZoom,
      gridPanOffset,
      autoResizeSpacesToFit,
      effectiveFootprintPolygons
    ]
  );

  const isHybrid = useMemo(
    () =>
      isHybridOnlySystemPreference(
        projectSettings.find((ps) => ps.code === 'system_preference')?.value
      ),
    [projectSettings]
  );

  const shouldGenerateHybridFullLevelSpaces = useMemo(
    () => needsHybridFullLevelSpaceGeneration(structure, footprint, isHybrid),
    [structure, footprint, isHybrid]
  );

  useEffect(() => {
    if (!isAddSpacesStep || !shouldGenerateHybridFullLevelSpaces) return;
    dispatch(autoGenerateHybridFullLevelSpacesThunk());
  }, [isAddSpacesStep, shouldGenerateHybridFullLevelSpaces, dispatch]);

  const shouldAutoMirror = useMemo(
    () => needsAutoMirror(structure),
    [structure]
  );

  useEffect(() => {
    if (isHybrid) return;
    if (hasAutoMirroredUnconditionedLevelsRef.current) return;
    if (!isAddSpacesStep || !shouldAutoMirror) return;
    dispatch(autoMirrorOpenSpacesToUnconditionedLevels());
    hasAutoMirroredUnconditionedLevelsRef.current = true;
  }, [isAddSpacesStep, shouldAutoMirror, isHybrid, dispatch]);

  const isDuctedSystem = useMemo(
    () => isDuctedSystemSelected(projectSettings),
    [projectSettings]
  );

  const shouldGenerateFullDuctworkSpaces = useMemo(
    () =>
      needsFullDuctworkSpaceGeneration(structure, footprint, isDuctedSystem),
    [structure, footprint, isDuctedSystem]
  );

  useEffect(() => {
    if (hasGeneratedFullDuctworkSpacesRef.current) return;
    if (!isAddSpacesStep || !shouldGenerateFullDuctworkSpaces) return;
    dispatch(autoGenerateFullDuctworkSpaces());
    hasGeneratedFullDuctworkSpacesRef.current = true;
  }, [isAddSpacesStep, shouldGenerateFullDuctworkSpaces, dispatch]);

  const handleImageDropForCrop = useCallback(
    (attachmentId: number, levelUniqueId: string) => {
      const attachment = structureAttachments.find(
        (a) => a.id === attachmentId
      );
      if (!attachment) return;
      setCropOnDropData({
        attachmentId,
        attachmentUrl: attachment.url ?? null,
        attachmentName: attachment.name,
        targetLevelUniqueId: levelUniqueId
      });
      setCropOnDropOpen(true);
    },
    [structureAttachments]
  );

  const handleStructureImagesUpload = useCallback(async (files: File[]) => {
    const { structureId: sid, structureUniqueId: suid } =
      store.getState().structure;
    if (!sid || !suid || files.length === 0) return;

    setIsUploadingImages(true);
    try {
      const formData = new FormData();
      formData.append('structureUniqueId', suid);

      const processedFiles = await Promise.all(
        files.map((file) =>
          file.type.startsWith('image/') ? resizeImageIfNeeded(file) : file
        )
      );

      for (const file of processedFiles) {
        formData.append('images', file);
      }

      await axios.post('structure/structure-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await loadStructureAttachments(sid);
    } catch (error) {
      console.error('Error uploading structure images:', error);
    } finally {
      setIsUploadingImages(false);
    }
  }, []);

  const propertyCoordinates = useMemo(
    () => resolvePropertyCoordinates(propertyLocation, attomData),
    [propertyLocation, attomData]
  );

  const pullSatelliteImageToAttachments = useCallback(async () => {
    const { structureId: sid, structureUniqueId: suid } =
      store.getState().structure;
    if (!propertyCoordinates || !sid || !suid) return;

    try {
      const satelliteFile = await fetchSatelliteImageAsFile(
        propertyCoordinates.lat,
        propertyCoordinates.lng,
        SATELLITE_IMAGE_FILE_NAME
      );

      const formData = new FormData();
      formData.append('structureUniqueId', suid);
      formData.append('images', satelliteFile);
      appendSatelliteViewToFormData(formData, {
        lat: propertyCoordinates.lat,
        lng: propertyCoordinates.lng,
        zoom: SATELLITE_DEFAULT_ZOOM,
        heading: 0
      });

      await axios.post('structure/structure-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await loadStructureAttachments(sid);
      dispatch(
        setSatelliteView({
          lat: propertyCoordinates.lat,
          lng: propertyCoordinates.lng,
          zoom: SATELLITE_DEFAULT_ZOOM,
          heading: 0
        })
      );
    } catch (error) {
      console.error('Error fetching satellite image:', error);
    }
  }, [dispatch, propertyCoordinates]);

  useEffect(() => {
    if (!isImagesStep) return;
    if (!propertyCoordinates || !structureUniqueId) return;
    if (satellitePullAttemptedRef.current === structureUniqueId) return;

    satellitePullAttemptedRef.current = structureUniqueId;

    const alreadyHasSatellite = structureAttachments.some(
      (a) => a.name === SATELLITE_IMAGE_NAME
    );
    if (alreadyHasSatellite) return;

    pullSatelliteImageToAttachments();
  }, [
    isImagesStep,
    propertyCoordinates,
    structureUniqueId,
    structureAttachments,
    pullSatelliteImageToAttachments
  ]);

  const renderRightContent = () => {
    if (isImagesStep) {
      return (
        <div className='flex h-full w-full justify-center overflow-hidden'>
          <div className='flex h-full w-full max-w-6xl items-center gap-4'>
            <div
              id='layout-images-upload'
              className='flex max-h-full w-1/2 max-w-[560px] flex-col overflow-y-auto rounded-lg border border-gray-200 bg-white'
            >
              <LayoutImagesUploadStep
                onImageUpload={handleStructureImagesUpload}
                onOpenPhotoCollector={() => setPhotoCollectorOpen(true)}
                isUploading={isUploadingImages}
              />
            </div>
            <ArrowRightIcon className='h-8 w-8 shrink-0 text-gray-300' />
            <div
              id='layout-images-levels'
              className='flex max-h-full min-h-0 flex-1 flex-col overflow-y-auto'
            >
              <LayoutImagesList onImageDropForCrop={handleImageDropForCrop} />
            </div>
          </div>
        </div>
      );
    }

    if (isOnLayoutSection) {
      return (
        <>
          <LevelPicker
            currentSectionId={currentSectionId}
            onLevelClick={
              isAddSpacesStep || isSpaceDetailsStep
                ? handleLayoutLevelClick
                : undefined
            }
          />
          <div className='absolute right-2 top-2 z-20 flex flex-col items-end gap-2 2xl:flex-row 2xl:items-start 2xl:gap-3'>
            <Popover className='relative'>
              <Popover.Button className='relative inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'>
                <AdjustmentsHorizontalIcon className='h-7 w-7' />
                <Tooltip text='Settings' />
              </Popover.Button>
              <Popover.Panel className='absolute right-0 top-12 z-30 w-64 rounded-lg bg-white p-4 shadow-lg'>
                {() => (
                  <div className='flex flex-col gap-4'>
                    <div className='flex flex-col gap-2'>
                      <div className='flex items-center justify-between'>
                        <span className='text-sm font-medium text-gray-700'>
                          Spaces
                        </span>
                        <span className='text-sm text-gray-500'>
                          {spaceOpacity}%
                        </span>
                      </div>
                      <input
                        type='range'
                        min='0'
                        max='100'
                        value={spaceOpacity}
                        onChange={(e) =>
                          setSpaceOpacity(Number.parseInt(e.target.value, 10))
                        }
                        className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600'
                      />
                    </div>
                    {!show3DView && (
                      <>
                        <div className='flex flex-col gap-2'>
                          <div className='flex items-center justify-between'>
                            <span className='text-sm font-medium text-gray-700'>
                              Grid Lines
                            </span>
                            <span className='text-sm text-gray-500'>
                              {gridLinesOpacity}%
                            </span>
                          </div>
                          <input
                            type='range'
                            min='0'
                            max='100'
                            value={gridLinesOpacity}
                            onChange={(e) =>
                              setGridLinesOpacity(
                                Number.parseInt(e.target.value, 10)
                              )
                            }
                            className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600'
                          />
                        </div>
                        <div className='flex flex-col gap-2'>
                          <div className='flex items-center justify-between'>
                            <span className='text-sm font-medium text-gray-700'>
                              {selectedLevelIndex === 0
                                ? 'Spaces Above'
                                : 'Spaces Below'}
                            </span>
                            <span className='text-sm text-gray-500'>
                              {overlaySpacesOpacity}%
                            </span>
                          </div>
                          <input
                            type='range'
                            min='0'
                            max='100'
                            value={overlaySpacesOpacity}
                            onChange={(e) =>
                              setOverlaySpacesOpacity(
                                Number.parseInt(e.target.value, 10)
                              )
                            }
                            className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600'
                          />
                        </div>
                        {currentLayoutImageAttachmentId !== null && (
                          <div className='flex flex-col gap-2'>
                            <div className='flex items-center justify-between'>
                              <span className='text-sm font-medium text-gray-700'>
                                Layout Underlay
                              </span>
                              <span className='text-sm text-gray-500'>
                                {layoutUnderlayOpacity}%
                              </span>
                            </div>
                            <input
                              type='range'
                              min='0'
                              max='100'
                              value={layoutUnderlayOpacity}
                              onChange={(e) =>
                                setLayoutUnderlayOpacity(
                                  Number.parseInt(e.target.value, 10)
                                )
                              }
                              className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600'
                            />
                          </div>
                        )}
                        <label className='flex cursor-pointer items-center gap-2 text-sm text-gray-700'>
                          <input
                            type='checkbox'
                            checked={autoGenerateSizingItems}
                            onChange={(e) =>
                              dispatch(
                                updateProjectSetting({
                                  attributeCode:
                                    'auto_generate_structure_sizing',
                                  value: e.target.checked ? 'Yes' : 'No'
                                })
                              )
                            }
                            className='h-4 w-4 rounded border-gray-300 text-blue-600'
                          />
                          Auto-generate walls/ceilings/floors
                        </label>
                        <label className='flex cursor-pointer items-center gap-2 text-sm text-gray-700'>
                          <input
                            type='checkbox'
                            checked={autoApplySpaceAboveAndBelow}
                            onChange={(e) =>
                              dispatch(
                                updateProjectSetting({
                                  attributeCode:
                                    'auto_apply_space_above_and_below',
                                  value: e.target.checked ? 'Yes' : 'No'
                                })
                              )
                            }
                            className='h-4 w-4 rounded border-gray-300 text-blue-600'
                          />
                          Auto-apply space above/below
                        </label>
                        <label className='flex cursor-pointer items-center gap-2 text-sm text-gray-700'>
                          <input
                            type='checkbox'
                            checked={autoResizeSpacesToFit}
                            onChange={(e) =>
                              setAutoResizeSpacesToFit(e.target.checked)
                            }
                            className='h-4 w-4 rounded border-gray-300 text-blue-600'
                          />
                          Auto-resize spaces to fit
                        </label>
                      </>
                    )}
                    {show3DView && (
                      <div className='flex flex-col gap-2'>
                        <div className='flex items-center justify-between'>
                          <span className='text-sm font-medium text-gray-700'>
                            Other Levels Opacity
                          </span>
                          <span className='text-sm text-gray-500'>
                            {inactiveLevelOpacity}%
                          </span>
                        </div>
                        <input
                          type='range'
                          min='0'
                          max='100'
                          value={inactiveLevelOpacity}
                          onChange={(e) =>
                            setInactiveLevelOpacity(
                              Number.parseInt(e.target.value, 10)
                            )
                          }
                          className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-blue-600'
                        />
                      </div>
                    )}
                  </div>
                )}
              </Popover.Panel>
            </Popover>
            <button
              type='button'
              className='relative inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              onClick={() => setShow3DView(!show3DView)}
            >
              {show3DView ? (
                <Squares2X2Icon className='h-7 w-7' />
              ) : (
                <CubeIcon className='h-7 w-7' />
              )}
              <Tooltip text={show3DView ? '2D View' : '3D View'} />
            </button>
            {(currentSectionId === 'orientation' ||
              currentSectionId === 'condenser_locations') && (
              <Compass enlarged />
            )}
            {showRecenterButton &&
              !show3DView &&
              (effectiveFootprintPolygons.length > 0 ||
                !!layoutImageForCurrentLevel ||
                getGridSpacesForLevelCallback(selectedLevelIndex).length >
                  0) && (
                <button
                  type='button'
                  className='relative order-last inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 2xl:order-first'
                  onClick={handleRecenter}
                >
                  <MapPinIcon className='h-7 w-7' />
                  <Tooltip
                    text={formatShortcutLabel(
                      'Recenter View',
                      RECENTER_VIEW_SHORTCUT_KEY
                    )}
                  />
                </button>
              )}
          </div>
          {show3DView ? (
            <div className='relative h-full w-full overflow-hidden rounded-lg border border-gray-200'>
              <Grid3DScene
                spaces={spaces3D}
                objects={allObjects3D}
                structure={structure}
                activeLevelIndex={selectedLevelIndex}
                inactiveLevelOpacity={inactiveLevelOpacity / 100}
                spaceOpacity={spaceOpacity}
              />
              <View3DHelpPopup />
            </div>
          ) : (
            <>
              <KeyboardMouseShortcutsPopup />
              <div className='relative h-full w-full overflow-hidden'>
                <StructureGrid
                  ref={structureGridRef}
                  gridSpaceOpacity={spaceOpacity}
                  gridSpaces={displayGridSpaces}
                  overlaySpaces={
                    isFootprintStep
                      ? []
                      : getOverlaySpacesForLevelCallback(selectedLevelIndex)
                  }
                  objects={getGridObjectsForLevelCallback(selectedLevelIndex)}
                  draggedSpaceIds={draggedSectionSpaces.map((s) => s.id)}
                  lineSets={[]}
                  configurationId={configurations[0]?.uniqueId}
                  mode={
                    isSpaceDetailsStep && currentSpaceDetailsCategory
                      ? getGridModeForSpaceDetailsCategory(
                          currentSpaceDetailsCategory
                        )
                      : 'summary'
                  }
                  showSpaceBtus={
                    isAfterAddSpacesStep &&
                    currentSpaceDetailsCategory !== 'ductwork'
                  }
                  layoutUnderlay={
                    activeFootprintMode === 'image' ||
                    activeFootprintMode === 'scale' ||
                    layoutUnderlayOpacity > 0
                  }
                  layoutImage={
                    activeFootprintMode === 'image' ||
                    activeFootprintMode === 'scale' ||
                    layoutUnderlayOpacity > 0
                      ? layoutImageForCurrentLevel
                      : undefined
                  }
                  layoutImageTransform={displayImageTransform}
                  layoutUnderlayOpacity={
                    activeFootprintMode === 'image' ||
                    activeFootprintMode === 'scale'
                      ? Math.max(layoutUnderlayOpacity, 50)
                      : layoutUnderlayOpacity
                  }
                  gridLinesOpacity={gridLinesOpacity}
                  overlaySpacesOpacity={overlaySpacesOpacity}
                  selectedSpaceIds={selectedSpaceIds}
                  onSpaceSelect={handleSpaceSelect}
                  onMarqueeSelect={handleMarqueeSelect}
                  onClearSelection={handleClearSelection}
                  onMultiSpaceResize={handleMultiSpaceResize}
                  onLayoutImageTransformChange={
                    handleLayoutImageTransformChange
                  }
                  onLayoutImageTransformEnd={handleLayoutImageTransformEnd}
                  isLayoutImageSelected={
                    allowImageInteraction &&
                    (activeFootprintMode === 'image' || isLayoutImageSelected)
                  }
                  onLayoutImageSelect={
                    allowImageInteraction
                      ? () => setIsLayoutImageSelected(true)
                      : undefined
                  }
                  onLayoutImageDeselect={() => setIsLayoutImageSelected(false)}
                  onLayoutImageLoad={handleLayoutImageLoad}
                  zoom={gridZoom}
                  onZoomChange={handleZoomChange}
                  panOffset={gridPanOffset}
                  onPanOffsetChange={handlePanOffsetChange}
                  footprintPolygons={displayFootprintPolygons}
                  levelBelowFootprint={levelBelowFootprint?.polygons ?? null}
                  levelAboveFootprint={levelAboveFootprint?.polygons ?? null}
                  footprintMode={activeFootprintMode}
                  footprintDrawingResetKey={drawingResetKey}
                  footprintShapes={effectiveFootprintShapes}
                  onFootprintShapeChange={handleFootprintShapeChange}
                  onDeleteFootprintShape={handleDeleteFootprintShape}
                  onFootprintDragStart={handleFootprintDragStart}
                  onFootprintDragEnd={handleFootprintDragEnd}
                  onFootprintScaleTransformChange={
                    handleFootprintScaleTransformChange
                  }
                  onFootprintScaleTransformEnd={
                    handleFootprintScaleTransformEnd
                  }
                  onFootprintStrokeComplete={handleFootprintStrokeComplete}
                  showFootprintArea={isFootprintStep}
                  spaceDrawingMode={isDrawingSpace}
                  onSpaceStrokeComplete={handleSpaceStrokeComplete}
                  pendingDrawnSpace={pendingDrawnSpace?.vertices ?? null}
                  showRuler
                />
                {isFootprintStep && (
                  <div className='absolute bottom-2 left-1/2 z-20 flex -translate-x-1/2 gap-2'>
                    {effectiveFootprintShapes.length > 0 && (
                      <button
                        type='button'
                        className='relative inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                        onClick={() => handleRotateFootprint('left')}
                      >
                        <RotateLeftIcon className='h-6 w-6' />
                        Rotate Left
                      </button>
                    )}
                    {effectiveFootprintShapes.length > 0 && (
                      <button
                        type='button'
                        className='relative inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                        onClick={() => handleRotateFootprint('right')}
                      >
                        <RotateRightIcon className='h-6 w-6' />
                        Rotate Right
                      </button>
                    )}
                    {levelBelowFootprint && (
                      <button
                        type='button'
                        className='relative inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                        onClick={handleCopyLevelBelow}
                      >
                        <DocumentDuplicateIcon className='h-6 w-6' />
                        Copy Floor Below
                      </button>
                    )}
                    {levelAboveFootprint && (
                      <button
                        type='button'
                        className='relative inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                        onClick={handleCopyLevelAbove}
                      >
                        <DocumentDuplicateIcon className='h-6 w-6' />
                        Copy Floor Above
                      </button>
                    )}
                  </div>
                )}
                {currentSectionId === 'condenser_locations' && (
                  <CondenserLocationsOverlay
                    gridSpaces={getGridSpacesForLevelCallback(
                      selectedLevelIndex
                    )}
                    zoom={gridZoom}
                    panOffset={gridPanOffset}
                    footprintPolygons={effectiveFootprintPolygons}
                  />
                )}
                {currentSectionId === 'orientation' && (
                  <SunDirectionOverlay
                    gridSpaces={getGridSpacesForLevelCallback(
                      selectedLevelIndex
                    )}
                    zoom={gridZoom}
                    panOffset={gridPanOffset}
                    footprintPolygons={effectiveFootprintPolygons}
                  />
                )}
                {isFootprintStep && (
                  <FootprintModeToolbar
                    mode={footprintMode}
                    showImageMode={currentLayoutImageAttachmentId !== null}
                    onChange={(mode) => {
                      setFootprintMode(mode);
                      if (mode !== 'image') {
                        setIsLayoutImageSelected(false);
                      }
                    }}
                  />
                )}
                {isAddSpacesStep && (
                  <SpaceDrawModeToolbar
                    mode={spaceDrawMode}
                    onChange={(mode) => {
                      setSpaceDrawMode(mode);
                      if (mode === 'draw') {
                        handleClearSelection();
                      }
                    }}
                  />
                )}
                <div className='absolute bottom-2 right-2 z-20 flex items-center gap-2'>
                  <button
                    type='button'
                    className='relative inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-default disabled:opacity-40'
                    onClick={undo}
                    disabled={!canUndo}
                  >
                    <ArrowUturnLeftIcon className='h-7 w-7' />
                    <Tooltip text={`Undo (${modKey}+Z)`} />
                  </button>
                  <button
                    type='button'
                    className='relative inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-default disabled:opacity-40'
                    onClick={redo}
                    disabled={!canRedo}
                  >
                    <ArrowUturnRightIcon className='h-7 w-7' />
                    <Tooltip text={`Redo (${modKey}+Shift+Z)`} />
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      );
    }

    return null;
  };

  const isLevelPagerStep = currentSectionId === 'configure_level_details';
  const levelPagerLevels = isLevelPagerStep
    ? getPagerLevelsForStep(structure, currentSectionId)
    : [];
  const levelPagerIndex = isLevelPagerStep
    ? Math.min(
        Math.max(activeLevelDetailIndex, 0),
        Math.max(levelPagerLevels.length - 1, 0)
      )
    : 0;
  const levelPagerCanAdvance =
    !!levelPagerLevels[levelPagerIndex] &&
    levelHasRequiredLevelDetailsAttributes(levelPagerLevels[levelPagerIndex]);

  const subStepConfig = (() => {
    if (isFootprintStep) {
      const orderedIndices = getFootprintStepLevelIndices(structure);
      return {
        count: orderedIndices.length,
        index: getFootprintStepSubStepIndex(structure, selectedLevelIndex),
        canAdvance: isFootprintLevelComplete(structure[selectedLevelIndex]),
        onChange: (index: number) => {
          const nextLevelIndex = orderedIndices[index];
          if (nextLevelIndex !== undefined) {
            dispatch(setActiveLevelIndex(nextLevelIndex));
          }
        }
      };
    }
    if (isLevelPagerStep) {
      return {
        count: levelPagerLevels.length,
        index: levelPagerIndex,
        canAdvance: levelPagerCanAdvance,
        onChange: (index: number) => dispatch(setActiveLevelDetailIndex(index))
      };
    }
    if (isAddSpacesStep) {
      const currentLevel = addSpacesNavigableLevels[addSpacesIndex];
      return {
        count: addSpacesNavigableLevels.length,
        index: addSpacesIndex,
        canAdvance: !!currentLevel && isAddSpacesLevelComplete(currentLevel),
        onChange: (index: number) => dispatch(setActiveSubStepIndex(index))
      };
    }
    if (isSpaceDetailsStep) {
      return {
        count: spaceDetailsSubSteps.length,
        index: spaceDetailsIndex,
        canAdvance: true,
        onChange: (index: number) => dispatch(setActiveSubStepIndex(index))
      };
    }
    return undefined;
  })();

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={openingAwareCollisionDetection}
      modifiers={[snapModifier]}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className='mx-auto flex h-full max-w-[1700px] flex-col gap-5'>
        <DndProvider backend={HTML5Backend}>
          <div className='min-h-0 grow'>
            <StepSidebar
              sections={sidebarSections}
              currentSectionId={currentSectionId}
              setCurrentSectionId={setCurrentSectionId}
              panelWidth={280}
              centeredHeader
              hideProgressBar={hideBasicInfoProgressBar}
              footerContent={
                isFootprintStep && hasAnyFootprint ? (
                  <FootprintResetButton onReset={handleResetFootprint} />
                ) : undefined
              }
              mainRightPanel={
                hideSizingAccuracyStepIds.includes(
                  currentSectionId
                ) ? undefined : (
                  <StructureMetricsDashboard
                    footprintMode={metricsFootprintMode}
                  />
                )
              }
              rightContent={renderRightContent()}
              previousTab={structurePreviousTab}
              nextTab={structureNextTab}
              onNext={stepSidebarOnNext}
              nextButtonText={isSimpleSizingStep ? 'Next Section' : undefined}
              nextVariant={isSimpleSizingStep ? 'green' : undefined}
              secondaryButton={detailedEstimateButton}
              subStepCount={subStepConfig?.count}
              subStepIndex={subStepConfig?.index}
              subStepCanAdvance={subStepConfig?.canAdvance}
              onSubStepChange={subStepConfig?.onChange}
              tabs={isSpaceDetailsStep ? spaceDetailsTabs : undefined}
              activePanelTab={
                isSpaceDetailsStep ? currentSpaceDetailsCategory : undefined
              }
              onPanelTabChange={
                isSpaceDetailsStep ? handleSpaceDetailsTabChange : undefined
              }
            />
          </div>
        </DndProvider>
      </div>
      <StructureDragOverlay
        activeDragData={activeDragData}
        draggedSectionSpaces={draggedSectionSpaces}
        getActiveDraggedItem={getActiveDraggedItemCallback}
        zoom={gridZoom}
      />
      <AttributeModal
        openState={attributeModalState}
        currentSectionId={currentSectionId}
      />
      <CropImageModal
        openState={[cropOnDropOpen, setCropOnDropOpen]}
        attachmentId={cropOnDropData.attachmentId}
        attachmentUrl={cropOnDropData.attachmentUrl}
        attachmentName={cropOnDropData.attachmentName}
        targetLevelUniqueId={cropOnDropData.targetLevelUniqueId}
      />
      <SendPhotoCollectorModal
        openState={[photoCollectorOpen, setPhotoCollectorOpen]}
      />
      <SpaceTypePickerModal
        openState={spaceTypePickerOpenState}
        areaSqft={pendingDrawnSpace?.areaSqft ?? 0}
        onSelect={handleDrawnSpaceTypeSelect}
        onCancel={handleCancelDrawnSpace}
      />
      <AddEquipmentModal />
      {/* Mount only while a conflict is live: effective + calc are then
          guaranteed non-null for the dialog's cards. */}
      {structureTonnage.effective && structureTonnage.calc && (
        <TonnageConflictDialog
          openState={tonnageConflictOpenState}
          effective={structureTonnage.effective}
          calc={structureTonnage.calc}
          record={structureTonnage.record}
          onResolve={handleTonnageConflictResolve}
        />
      )}
    </DndContext>
  );
}
